import React, {
  useMemo
} from "react";

import {
  uniqBy
} from 'lodash';

import {
  Heading,
  VStack,
  HStack,
  Text,
  AccordionIcon,
  AccordionButton,
  AccordionPanel,
  AccordionItem,
  Flex,
  StatGroup,
} from "@chakra-ui/react"

import { QuestionMinimalFragment, AnswerForMappingFragment } from "../../graphql/generated/definitions";
import Link from "../utils/Link";
import useTranslations from "../../hooks/useTranslations";


import AnalyticsQuestionStat from './AnalyticsQuestionStat';

export type AnalyticsQuestionItemProps = {
  question: QuestionMinimalFragment,
  answers: AnswerForMappingFragment[],
  type: string,
  href?: string,
  populationCount: number
};

const AnalyticsQuestionItem: React.FC<AnalyticsQuestionItemProps> = ({
  question,
  answers,
  populationCount,
  href = null
}) => {
  const t = useTranslations();

  const uniqAnswers = useMemo(() => (
    uniqBy(answers, 'collaboratorId')
  ), [answers]);

  const populationPart = Math.round((uniqAnswers.length / populationCount) * 100);

  return (
    <AccordionItem
      data-test-id={`analytics-question-item-${question.id}`}
    >
      <AccordionButton>
        <Flex
          flex="1"
          textAlign="left"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading 
            size="xs" 
            fontWeight="medium"
          >
            {question.label}
          </Heading>

          <HStack w="20%" justifyContent="space-between">
            {
              [
                {
                  label: t("statistics.population"),
                  value: uniqAnswers.length
                },
                {
                  label: t("statistics.rate"),
                  value: `${populationPart}%`
                }
              ].map(({ label, value }, index) => (
                <VStack
                  spacing={0}
                  flex={1}
                  justifyContent="center"
                  key={index}
                >
                  <Text 
                    fontSize="xs" 
                    fontWeight="light"
                  >
                    {label}
                  </Text>
                  <Text fontWeight="medium">
                    {value}
                  </Text>
                </VStack>
              ))
            }
          </HStack>
        </Flex>
        <AccordionIcon />
      </AccordionButton>
      <AccordionPanel pb={4}>
        <StatGroup>
          {
            question.options.map(({ label, value }) => (
              <AnalyticsQuestionStat
                key={label}
                answers={uniqAnswers}
                label={label}
                value={value}
                populationCount={populationCount}
              />
            ))
          }
        </StatGroup>

        {
          href && 
          (
            <Flex justifyContent="center">
              <Link
                to={href}
              >
                {t("statistics.details")}
              </Link>
            </Flex>
          )
        }
      </AccordionPanel>
    </AccordionItem>
  )
};

export default React.memo(AnalyticsQuestionItem);